import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { tpoGenUrls } from 'config/urls';
import { PAGE_LIMIT } from '../../config/metricConfig';
import type { AttributeOption, AttributeRequest, AttributeResponse, FiltersPayload } from '../../types';

const SEARCH_DEBOUNCE_MS = 300;

interface UseAttributeOptionsResult {
    options: AttributeOption[];
    isPending: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    totalCount: number;
}

const fetchAttributeOptions = async (
    planId: string,
    body: AttributeRequest,
    signal: AbortSignal,
): Promise<AttributeResponse> => {
    let token = '';
    try {
        token = localStorage.getItem('accessToken') ?? '';
    } catch {
        // incognito / disabled storage; fall through with empty token
    }
    const response = await axios(tpoGenUrls.getReportDataFilterAttributes(planId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
        data: body,
    });
    const data = response.data;
    const attributeType = body.attribute_type;
    const names: string[] = data?.attribute_names ?? [];
    const options: AttributeOption[] = names.map((name) => ({
        attributeType,
        attributeDesc: name,
        attributeExId: name,
        dimension: attributeType,
    }));
    return {
        options,
        total_count: data?.total_count ?? 0,
    };
};

const useAttributeOptions = (
    planId: string,
    attributeType: string | null,
    filters: FiltersPayload,
    searchKey: string,
): UseAttributeOptionsResult => {
    const [debouncedSearchKey, setDebouncedSearchKey] = useState<string>(searchKey);

    const debouncedSetter = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedSearchKey(value);
            }, SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => {
        debouncedSetter(searchKey);
        return () => {
            debouncedSetter.cancel();
        };
    }, [searchKey, debouncedSetter]);

    const enabled = !!attributeType && !!planId;

    const { data, isPending, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery({
        queryKey: ['metricAttributeOptions', planId, attributeType, filters, debouncedSearchKey],
        queryFn: ({ signal, pageParam }) =>
            fetchAttributeOptions(
                planId,
                {
                    attribute_type: attributeType ?? '',
                    limit: PAGE_LIMIT,
                    filters: filters ?? null,
                    offset: pageParam,
                    search_key: debouncedSearchKey,
                },
                signal,
            ),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _pages, lastPageParam) => {
            const nextOffset = lastPageParam + PAGE_LIMIT;
            return nextOffset < (lastPage.total_count ?? 0) ? nextOffset : undefined;
        },
        enabled,
    });

    const options = useMemo(() => {
        if (!data?.pages?.length) return [];
        const all: AttributeOption[] = [];
        for (const page of data.pages) {
            all.push(...page.options);
        }
        return all;
    }, [data]);

    return {
        options,
        isPending: enabled ? isPending : false,
        isFetching,
        isFetchingNextPage,
        fetchNextPage: () => fetchNextPage(),
        hasNextPage: hasNextPage ?? false,
        totalCount: data?.pages?.[0]?.total_count ?? 0,
    };
};

export default useAttributeOptions;
